/**
 * Agora SDK Integration
 *
 * This module provides Agora-based video conferencing that replaces VideoSDK.
 * The API is designed to mirror VideoSDK's interface for easier migration.
 *
 * Uses agora-rtc-react SDK v2.5.1 for React-native hooks and components.
 */

// Import from the new agora-rtc-react based context
import {
  AgoraMeetingProvider as AgoraReactMeetingProvider,
  useAgoraMeeting as useAgoraReactMeeting,
  useAgoraParticipant as useAgoraReactParticipant,
  useAgoraMediaDevice as useAgoraReactMediaDevice,
  AgoraConstants as AgoraReactConstants,
  Constants as ReactConstants,
  LocalUser,
  RemoteUser,
  AgoraRTCProvider,
} from '../context/AgoraReactContext';

// Import from the legacy context (fallback)
import {
  AgoraMeetingProvider as AgoraLegacyMeetingProvider,
  useAgoraMeeting as useAgoraLegacyMeeting,
  useAgoraParticipant as useAgoraLegacyParticipant,
  useAgoraMediaDevice as useAgoraLegacyMediaDevice,
  AgoraConstants as AgoraLegacyConstants,
  Constants as LegacyConstants,
} from '../context/AgoraContext';

// Feature flag: Set to false to use the legacy (stable) context
// Set to true to use the new agora-rtc-react SDK (experimental)
// NOTE: Set to false temporarily - Agora App ID appears invalid, need to check Agora Console
const USE_AGORA_REACT_SDK = false;

// Export based on feature flag
export const AgoraMeetingProvider = USE_AGORA_REACT_SDK ? AgoraReactMeetingProvider : AgoraLegacyMeetingProvider;
export const useAgoraMeeting = USE_AGORA_REACT_SDK ? useAgoraReactMeeting : useAgoraLegacyMeeting;
export const useAgoraParticipant = USE_AGORA_REACT_SDK ? useAgoraReactParticipant : useAgoraLegacyParticipant;
export const useAgoraMediaDevice = USE_AGORA_REACT_SDK ? useAgoraReactMediaDevice : useAgoraLegacyMediaDevice;
export const AgoraConstants = USE_AGORA_REACT_SDK ? AgoraReactConstants : AgoraLegacyConstants;
export const Constants = USE_AGORA_REACT_SDK ? ReactConstants : LegacyConstants;

// PubSub for real-time messaging
export { useAgoraPubSub } from '../hooks/useAgoraPubSub';

// Stub hooks for VideoSDK features without direct Agora equivalents
export {
  useAgoraTranscription,
  useAgoraTranslation,
  useAgoraWhiteboard,
  createAgoraCameraVideoTrack,
  createAgoraMicrophoneAudioTrack,
  getAgoraNetworkStats,
} from '../hooks/useAgoraStubs';

// Alias exports for stubs (more VideoSDK compatibility)
export {
  createAgoraMicrophoneAudioTrack as createMicrophoneAudioTrack,
  getAgoraNetworkStats as getNetworkStats,
} from '../hooks/useAgoraStubs';

// Components
export { default as AgoraVideoPlayer } from '../components/agora/AgoraVideoPlayer';

// Re-export AgoraRTC for advanced usage
export { default as AgoraRTC } from 'agora-rtc-sdk-ng';

// Export agora-rtc-react components for direct use
export { LocalUser, RemoteUser, AgoraRTCProvider };

// Alias exports for VideoSDK compatibility
export {
  AgoraMeetingProvider as MeetingProvider,
  useAgoraMeeting as useMeeting,
  useAgoraParticipant as useParticipant,
  useAgoraMediaDevice as useMediaDevice,
};

export { useAgoraPubSub as usePubSub } from '../hooks/useAgoraPubSub';

// Alias exports for stubs (VideoSDK compatibility)
export {
  useAgoraTranscription as useTranscription,
  useAgoraTranslation as useTranslation,
  useAgoraWhiteboard as useWhiteboard,
  createAgoraCameraVideoTrack as createCameraVideoTrack,
} from '../hooks/useAgoraStubs';
